import { ref } from 'vue'

// voiceState: 'IDLE' | 'LISTENING' | 'PROCESSING' | 'SPEAKING'
export function useVoiceMode({ userInput, addUserMessage, onVoiceDelta, onVoiceComplete, onVoiceToolCall, getConversationId }) {
  const voiceState = ref('IDLE')
  const liveTranscript = ref('')

  let mediaRecorder = null
  let micStream = null
  let websocket = null

  let ttsWs = null
  let ttsPlayStarted = false
  let interruptPending = false
  let keepAliveInterval = null
  let lastDgUpdateTime = 0
  let speakingStartTime = 0

  let wordQueue = []
  let wordInterval = null

  let pendingQuestion = ''       // Q1: saved when triggerSend fires, used for concatenation on interrupt
  let processingStartTime = 0   // when PROCESSING began; ignore early speech for 500ms

  // PCM 24 kHz WebAudio — ElevenLabs pcm_24000 output (16-bit signed LE mono)
  let pcmNextTime = 0
  let pcmSessionStart = 0
  let activePcmSources = []
  let pcmRemainder = null

  // AudioContext is created once per session in startVoice() and kept alive.
  // Chrome suspends any AudioContext created outside a user-gesture, so we
  // must not recreate it mid-session.
  let audioCtx = null
  let analyser = null
  let sourceNode = null
  let silenceInterval = null
  let hasSpeechSample = false
  let silenceSince = null
  let finalizedTranscript = ''
  let pendingFinal = false

  const SPEECH_THRESHOLD = 0.015
  const SILENCE_TRIGGER_MS = 1000
  const DG_GUARD_MS = 1000
  const DG_GUARD_PENDING_MS = 2000 // extra wait when is_final hasn't arrived yet

  // ── Silence detector ──────────────────────────────────────────────────────────

  const teardownAnalyser = () => {
    if (silenceInterval) { clearInterval(silenceInterval); silenceInterval = null }
    if (sourceNode) { try { sourceNode.disconnect() } catch (_) {}; sourceNode = null }
    if (analyser) { try { analyser.disconnect() } catch (_) {}; analyser = null }
  }

  const triggerSend = () => {
    const transcript = liveTranscript.value.trim()
    if (!transcript || voiceState.value !== 'LISTENING') return
    const guard = pendingFinal ? DG_GUARD_PENDING_MS : DG_GUARD_MS
    if (Date.now() - lastDgUpdateTime < guard) return
    stopSilenceDetector()
    liveTranscript.value = ''
    finalizedTranscript = ''
    pendingFinal = false
    pendingQuestion = transcript  // save Q1 for potential PROCESSING interrupt
    processingStartTime = Date.now()
    userInput.value = ''
    voiceState.value = 'PROCESSING'
    addUserMessage(transcript)
    try {
      openVoiceChatWs(transcript)
    } catch (e) {
      console.error('[Voice] Failed to open voice-chat WS:', e)
      voiceState.value = 'LISTENING'
      if (micStream) startSilenceDetector(micStream)
    }
    hasSpeechSample = false
    silenceSince = null
  }

  const startSilenceDetector = (stream, initialHasSpeech = false) => {
    teardownAnalyser()

    hasSpeechSample = initialHasSpeech
    silenceSince = null
    lastDgUpdateTime = Date.now()

    try {
      if (!audioCtx || audioCtx.state === 'closed') audioCtx = new AudioContext()
      audioCtx.resume().catch(() => {})

      analyser = audioCtx.createAnalyser()
      analyser.fftSize = 512
      sourceNode = audioCtx.createMediaStreamSource(stream)
      sourceNode.connect(analyser)
      const buf = new Float32Array(analyser.fftSize)

      silenceInterval = setInterval(() => {
        analyser.getFloatTimeDomainData(buf)
        const rms = Math.sqrt(buf.reduce((s, v) => s + v * v, 0) / buf.length)

        if (voiceState.value !== 'LISTENING') {
          hasSpeechSample = false
          silenceSince = null
          return
        }

        if (rms > SPEECH_THRESHOLD) {
          hasSpeechSample = true
          silenceSince = null
        } else if (hasSpeechSample && liveTranscript.value) {
          if (!silenceSince) silenceSince = Date.now()
          else if (Date.now() - silenceSince >= SILENCE_TRIGGER_MS) triggerSend()
        }
      }, 100)
    } catch (e) {
      console.warn('[Voice] Silence detector init failed:', e)
    }
  }

  const stopSilenceDetector = () => {
    teardownAnalyser()
    hasSpeechSample = false
    silenceSince = null
  }

  // ── PCM WebAudio helpers ──────────────────────────────────────────────────────

  const handleAudioChunk = (buf) => {
    if (!audioCtx) return
    let data = buf
    if (pcmRemainder) {
      const joined = new Uint8Array(pcmRemainder.byteLength + buf.byteLength)
      joined.set(new Uint8Array(pcmRemainder), 0)
      joined.set(new Uint8Array(buf), pcmRemainder.byteLength)
      data = joined.buffer
      pcmRemainder = null
    }
    if (data.byteLength % 2 !== 0) {
      pcmRemainder = data.slice(data.byteLength - 1)
      data = data.slice(0, data.byteLength - 1)
    }
    if (data.byteLength === 0) return
    const samples = new Int16Array(data)
    const floats = new Float32Array(samples.length)
    for (let i = 0; i < samples.length; i++) floats[i] = samples[i] / 32768.0

    const ab = audioCtx.createBuffer(1, floats.length, 24000)
    ab.copyToChannel(floats, 0)

    const src = audioCtx.createBufferSource()
    src.buffer = ab
    src.connect(audioCtx.destination)
    activePcmSources.push(src)

    const startAt = Math.max(audioCtx.currentTime, pcmNextTime)
    if (!ttsPlayStarted) {
      ttsPlayStarted = true
      voiceState.value = 'SPEAKING'
      speakingStartTime = Date.now()
      pcmSessionStart = startAt
    }
    src.start(startAt)
    pcmNextTime = startAt + ab.duration
  }

  // Waits for all scheduled PCM audio to finish, then returns to LISTENING.
  const finalizeTtsStream = () => {
    if (voiceState.value !== 'SPEAKING') return
    const remaining = audioCtx ? Math.max(0, pcmNextTime - audioCtx.currentTime) * 1000 : 0
    if (remaining > 50) { setTimeout(finalizeTtsStream, remaining + 100); return }
    stopWordScheduler()
    liveTranscript.value = ''
    finalizedTranscript = ''
    pendingFinal = false
    voiceState.value = 'LISTENING'
    if (micStream) startSilenceDetector(micStream)
  }

  // ── Word-sync helpers ─────────────────────────────────────────────────────────

  const processAlignment = ({ chars, charStartTimesMs, audioOffset }) => {
    if (!chars || !charStartTimesMs) return
    let word = ''
    let wordStartMs = audioOffset + charStartTimesMs[0]
    for (let i = 0; i < chars.length; i++) {
      const ch = chars[i]
      if (ch === ' ' || ch === '\n') {
        if (word) { wordQueue.push({ word, startMs: wordStartMs }); word = '' }
      } else {
        if (!word) wordStartMs = audioOffset + charStartTimesMs[i]
        word += ch
      }
    }
    if (word) wordQueue.push({ word, startMs: wordStartMs })
  }

  const startWordScheduler = () => {
    if (wordInterval) return
    wordInterval = setInterval(() => {
      if (!audioCtx || !pcmSessionStart) return
      const nowMs = (audioCtx.currentTime - pcmSessionStart) * 1000
      while (wordQueue.length && wordQueue[0].startMs <= nowMs) {
        onVoiceDelta(wordQueue.shift().word + ' ')
      }
    }, 30)
  }

  const stopWordScheduler = () => {
    if (wordInterval) { clearInterval(wordInterval); wordInterval = null }
    wordQueue = []
  }

  // Stops all PCM nodes and closes the voice-chat WS.
  // Nulls ttsWs handlers BEFORE closing so onclose does NOT run finalizeTtsStream.
  const stopCurrentAudio = () => {
    stopWordScheduler()
    if (ttsWs) {
      // Tell backend to cancel agent / stop TTS before closing
      if (ttsWs.readyState === WebSocket.OPEN) {
        try { ttsWs.send(JSON.stringify({ type: 'stop' })) } catch (_) {}
      }
      ttsWs.onopen = null
      ttsWs.onmessage = null
      ttsWs.onclose = null
      ttsWs.onerror = null
      try { ttsWs.close() } catch (_) {}
      ttsWs = null
    }
    activePcmSources.forEach(s => { try { s.stop() } catch (_) {} })
    activePcmSources = []
    pcmNextTime = 0
    pcmSessionStart = 0
    pcmRemainder = null
    ttsPlayStarted = false
  }

  const getWsBase = () => {
    const metaUrl = document.querySelector('meta[name="voice-ws-url"]')?.content
    return (metaUrl && !/localhost:8000/.test(metaUrl))
      ? metaUrl
      : window.location.origin.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:')
  }

  // ── Voice-Chat WebSocket ──────────────────────────────────────────────────────
  // One WS to /chat/ws/voice-chat handles LLM + ElevenLabs TTS server-side.
  // Binary frames = PCM 24 kHz audio; text frames = alignment/complete/tool_call.

  const openVoiceChatWs = (question) => {
    interruptPending = false   // clear guard so future interrupts are allowed
    ttsPlayStarted = false
    pcmNextTime = 0
    pcmSessionStart = 0
    pcmRemainder = null
    activePcmSources = []

    // Defensively clean up any previous ttsWs so its stale onclose
    // cannot null the new reference or reset voiceState.
    if (ttsWs) {
      ttsWs.onopen = null; ttsWs.onmessage = null
      ttsWs.onclose = null; ttsWs.onerror = null
      try { ttsWs.close() } catch (_) {}
      ttsWs = null
    }

    ttsWs = new WebSocket(`${getWsBase()}/chat/ws/voice-chat`)
    ttsWs.binaryType = 'arraybuffer'

    ttsWs.onopen = () => {
      ttsWs.send(JSON.stringify({
        question,
        conversation_id: getConversationId(),
        user_id: parseInt(localStorage.getItem('userId') || '0', 10),
        workspace_id: parseInt(localStorage.getItem('currentWorkspaceId') || '0', 10),
      }))
    }

    ttsWs.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        if (event.data.byteLength > 0) handleAudioChunk(event.data)
        return
      }
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'alignment') {
          processAlignment(msg)
          startWordScheduler()
        } else if (msg.type === 'complete') {
          stopWordScheduler()
          onVoiceComplete(msg.answer || '')
        } else if (msg.type === 'tool_call') {
          onVoiceToolCall(msg.display_name || '')
          if (voiceState.value === 'PROCESSING') { voiceState.value = 'SPEAKING'; speakingStartTime = Date.now() }
        } else if (msg.type === 'error') {
          console.error('[Voice] server error:', msg.message)
          if (voiceState.value === 'PROCESSING') {
            voiceState.value = 'LISTENING'
            if (micStream) startSilenceDetector(micStream)
          }
        }
      } catch (e) {
        console.error('[Voice] WS text parse error:', e)
      }
    }

    ttsWs.onclose = () => {
      ttsWs = null
      if (voiceState.value === 'PROCESSING') {
        // Backend closed WS before any audio (e.g. LLM error) — go back to listening
        console.warn('[Voice] WS closed while PROCESSING — returning to LISTENING')
        voiceState.value = 'LISTENING'
        if (micStream) startSilenceDetector(micStream)
        return
      }
      finalizeTtsStream()
    }

    ttsWs.onerror = (e) => console.error('[Voice] voice-chat WS error:', e)
  }

  // No-ops: voice-chat WS handles LLM+TTS server-side, SSE path is unused in voice mode.
  const onStreamingDelta = () => {}
  const onStreamingComplete = () => {}

  // ── Interrupt TTS ─────────────────────────────────────────────────────────────

  const interruptTTS = (combineQ2 = false) => {
    const q2 = liveTranscript.value.trim()
    liveTranscript.value = ''
    finalizedTranscript = ''
    pendingFinal = false
    stopCurrentAudio()

    if (combineQ2 && q2 && q2.split(/\s+/).length >= 2 && pendingQuestion) {
      const combined = pendingQuestion + '. ' + q2
      onVoiceComplete('__DISCARD__')
      pendingQuestion = combined
      processingStartTime = Date.now()
      voiceState.value = 'PROCESSING'
      addUserMessage(q2)
      try {
        openVoiceChatWs(combined)
      } catch (e) {
        console.error('[Voice] Failed to open voice-chat WS:', e)
        voiceState.value = 'LISTENING'
        if (micStream) startSilenceDetector(micStream)
      }
    } else {
      voiceState.value = 'LISTENING'
      if (micStream) startSilenceDetector(micStream, true)
    }
  }

  // ── Deepgram STT WebSocket ────────────────────────────────────────────────────

  // Null all handlers before intentional close so onclose never resets state to IDLE.
  const clearWsHandlers = () => {
    if (!websocket) return
    websocket.onopen = null
    websocket.onmessage = null
    websocket.onerror = null
    websocket.onclose = null
  }

  const attachWsHandlers = () => {
    websocket.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer || event.data instanceof Blob) return
      try {
        const data = JSON.parse(event.data)
        const transcript = data?.channel?.alternatives?.[0]?.transcript || ''
        if (transcript) {
          if (voiceState.value === 'SPEAKING') {
            // Always accumulate transcripts during SPEAKING
            if (data.is_final) {
              finalizedTranscript += transcript + ' '
              liveTranscript.value = finalizedTranscript.trimEnd()
              pendingFinal = false
            } else {
              liveTranscript.value = (finalizedTranscript + transcript).trimEnd()
              pendingFinal = true
            }
            // Finalized Q2 with 2+ words → interrupt with combine
            if (data.is_final && transcript.trim().split(/\s+/).length >= 2) {
              interruptTTS(true)
              return
            }
            // Normal SPEAKING interrupt: any speech after 1s guard
            if (Date.now() - speakingStartTime > 1000) interruptTTS(false)
          } else if (voiceState.value === 'PROCESSING') {
            // Show Q2 transcription bubble while user speaks during PROCESSING
            if (data.is_final) {
              finalizedTranscript += transcript + ' '
              liveTranscript.value = finalizedTranscript.trimEnd()
              pendingFinal = false
            } else {
              liveTranscript.value = (finalizedTranscript + transcript).trimEnd()
              pendingFinal = true
            }

            // Confirmed speech via Deepgram during PROCESSING → combine Q1+Q2 and resend
            if (data.is_final && transcript.trim().split(/\s+/).length >= 2 &&
                Date.now() - processingStartTime > 500) {
              const q2 = liveTranscript.value.trim() || transcript.trim()
              const combined = pendingQuestion + '. ' + q2
              // Close current voice-chat WS
              if (ttsWs) {
                ttsWs.onopen = null; ttsWs.onmessage = null
                ttsWs.onclose = null; ttsWs.onerror = null
                try { ttsWs.close() } catch (_) {}
                ttsWs = null
              }

              // Discard Q1 response if already added, then send combined
              onVoiceComplete('__DISCARD__')
              liveTranscript.value = ''
              finalizedTranscript = ''
              pendingFinal = false
              pendingQuestion = combined
              processingStartTime = Date.now()
              addUserMessage(q2)
              openVoiceChatWs(combined)
            }
          } else if (voiceState.value === 'LISTENING') {
            // Deepgram sends results in segments. Interim results update the
            // current segment; once is_final is true the segment is complete.
            // We accumulate finalized segments so long utterances aren't lost.
            if (data.is_final) {
              finalizedTranscript += transcript + ' '
              liveTranscript.value = finalizedTranscript.trimEnd()
              pendingFinal = false
            } else {
              liveTranscript.value = (finalizedTranscript + transcript).trimEnd()
              pendingFinal = true
            }
            lastDgUpdateTime = Date.now()
          }
        }
      } catch (e) {
        console.error('[Voice] WS parse error:', e)
      }
    }

    websocket.onerror = (e) => console.error('[Voice] WS error:', e)

    websocket.onclose = (e) => {
      stopSilenceDetector()
      websocket = null
      if (voiceState.value !== 'IDLE') {
        reconnectTranscribeWs()
      }
    }
  }

  const startMediaRecorder = () => {
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : ''
    mediaRecorder = new MediaRecorder(micStream, mimeType ? { mimeType } : {})
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0 && websocket?.readyState === WebSocket.OPEN)
        event.data.arrayBuffer().then(buf => websocket.send(buf))
    }
    mediaRecorder.start(250)
  }

  const reconnectTranscribeWs = () => {
    if (!micStream || voiceState.value === 'IDLE') return
    websocket = new WebSocket(`${getWsBase()}/chat/ws/transcribe`)
    websocket.binaryType = 'arraybuffer'
    websocket.onopen = () => {
      // Null ondataavailable FIRST so the final buffered chunk from the old recorder
      // never reaches the new WS (which would corrupt the fresh WebM stream).
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.ondataavailable = null
        mediaRecorder.stop()
      }
      startMediaRecorder()
    }
    attachWsHandlers()
    if (voiceState.value === 'LISTENING') startSilenceDetector(micStream)
  }

  const setupMicAndWs = async () => {
    micStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: false },
    })
    startSilenceDetector(micStream)

    websocket = new WebSocket(`${getWsBase()}/chat/ws/transcribe`)
    websocket.binaryType = 'arraybuffer'
    websocket.onopen = () => {
      startMediaRecorder()
      // Keep Deepgram WS alive so it doesn't close mid-PROCESSING
      keepAliveInterval = setInterval(() => {
        if (websocket && websocket.readyState === WebSocket.OPEN)
          websocket.send(JSON.stringify({ type: 'KeepAlive' }))
      }, 5000)
    }

    attachWsHandlers()
  }

  // ── Public API ────────────────────────────────────────────────────────────────

  const startVoice = async () => {
    if (voiceState.value !== 'IDLE') return
    voiceState.value = 'LISTENING'
    try {
      await setupMicAndWs()
    } catch (e) {
      console.error('[Voice] startVoice error:', e)
      stopSilenceDetector()
      voiceState.value = 'IDLE'
    }
  }

  const endVoice = () => {
    if (keepAliveInterval) { clearInterval(keepAliveInterval); keepAliveInterval = null }
    stopSilenceDetector()
    if (audioCtx) { audioCtx.close().catch(() => {}); audioCtx = null }
    if (mediaRecorder && mediaRecorder.state !== 'inactive') { mediaRecorder.stop(); mediaRecorder = null }
    if (micStream) { micStream.getTracks().forEach(t => t.stop()); micStream = null }
    clearWsHandlers()
    if (websocket) { websocket.close(); websocket = null }
    stopCurrentAudio()
    liveTranscript.value = ''
    finalizedTranscript = ''
    pendingFinal = false
    pendingQuestion = ''
    processingStartTime = 0
    voiceState.value = 'IDLE'
  }

  return { voiceState, liveTranscript, startVoice, endVoice, interruptTTS, onStreamingDelta, onStreamingComplete }
}
